import React, { useState } from 'react';
import { FaMoneyBill } from 'react-icons/fa';
import { calculatePriceFromNetto, calculatePriceFromBrutto } from './pricingUtils';
import BaseModal from '../common/BaseModal';
import { validators } from '../../utils/validation';

const DirectPriceModal = ({
  show,
  product,
  onClose,
  directPriceNetto,
  setDirectPriceNetto,
  directPriceBrutto,
  setDirectPriceBrutto,
  onApply,
  loading,
}) => {
  const [priceErrors, setPriceErrors] = useState({});

  if (!product) return null;

  const vatRate = product.stawka_vat || 23;

  const validatePrices = () => {
    const errors = {};
    const nettoErr = validators.positiveNumber(directPriceNetto, 'Cena netto');
    if (nettoErr) errors.netto = nettoErr;
    const bruttoErr = validators.positiveNumber(directPriceBrutto, 'Cena brutto');
    if (bruttoErr) errors.brutto = bruttoErr;
    setPriceErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleApply = () => {
    if (validatePrices()) {
      onApply();
    }
  };

  const footerButtons = (
    <>
      <button type="button" className="btn btn-secondary" onClick={onClose}>
        Anuluj
      </button>
      <button
        type="button"
        className="btn btn-success"
        onClick={handleApply}
        disabled={loading || !directPriceNetto || !directPriceBrutto}
      >
        {loading ? (
          <>
            <span className="spinner-border spinner-border-sm me-2"></span>
            Zapisywanie...
          </>
        ) : (
          'Zapisz cenę'
        )}
      </button>
    </>
  );

  return (
    <BaseModal
      isOpen={show}
      onClose={onClose}
      title={<><FaMoneyBill className="me-2" />Edycja ceny - {product.nazwa_uproszczona || product.nazwa}</>}
      size="md"
      footer={footerButtons}
    >
      <div className="mb-3">
        <div className="row">
          <div className="col-md-6">
            <strong>Kod produktu:</strong> {product.kod_produktu}
          </div>
          <div className="col-md-6">
            <strong>EAN:</strong> {product.ean || 'Brak'}
          </div>
        </div>
        <div className="row mt-2">
          <div className="col-md-6">
            <strong>VAT:</strong> {vatRate}%
          </div>
          <div className="col-md-6">
            <strong>Status:</strong> {product.hasSpecialPrice ?
              <span className="badge bg-warning">Cena specjalna</span> :
              <span className="badge bg-secondary">Cena domyślna</span>
            }
          </div>
        </div>
      </div>

      <hr />

      <div className="row g-3">
        <div className="col-md-6">
          <label className="form-label">Cena netto (zł):</label>
          <input
            type="number"
            className={`form-control${priceErrors.netto ? ' is-invalid' : ''}`}
            step="0.01"
            min="0"
            value={directPriceNetto}
            onChange={(e) => {
              setDirectPriceNetto(e.target.value);
              setPriceErrors(prev => { const n = {...prev}; delete n.netto; return n; });
              const newBrutto = calculatePriceFromNetto(e.target.value, vatRate);
              if (newBrutto) {
                setDirectPriceBrutto(newBrutto);
              }
            }}
            placeholder="0.00"
          />
          {priceErrors.netto && <div className="invalid-feedback">{priceErrors.netto}</div>}
        </div>
        <div className="col-md-6">
          <label className="form-label">Cena brutto (zł):</label>
          <input
            type="number"
            className={`form-control${priceErrors.brutto ? ' is-invalid' : ''}`}
            step="0.01"
            min="0"
            value={directPriceBrutto}
            onChange={(e) => {
              setDirectPriceBrutto(e.target.value);
              setPriceErrors(prev => { const n = {...prev}; delete n.brutto; return n; });
              const newNetto = calculatePriceFromBrutto(e.target.value, vatRate);
              if (newNetto) {
                setDirectPriceNetto(newNetto);
              }
            }}
            placeholder="0.00"
          />
          {priceErrors.brutto && <div className="invalid-feedback">{priceErrors.brutto}</div>}
        </div>
      </div>

      <div className="mt-3">
        <div className="form-text">
          <i className="fas fa-info-circle me-1"></i>
          Zmieniając jedną cenę, druga zostanie automatycznie przeliczona na podstawie stawki VAT.
        </div>
      </div>

      {/* Porównanie z aktualną ceną */}
      <div className="mt-3 p-3 bg-light rounded">
        <h6>Porównanie cen:</h6>
        <div className="row">
          <div className="col-md-6">
            <strong>Aktualna cena:</strong><br />
            <span className="text-muted">
              Netto: {product.hasSpecialPrice ?
                parseFloat(product.specialPriceNetto || 0).toFixed(2) :
                parseFloat(product.cena_sprzedazy_netto || 0).toFixed(2)} zł
            </span><br />
            <span className="text-muted">
              Brutto: {product.hasSpecialPrice ?
                parseFloat(product.specialPriceBrutto || 0).toFixed(2) :
                parseFloat(product.cena_sprzedazy_brutto || 0).toFixed(2)} zł
            </span>
          </div>
          <div className="col-md-6">
            <strong>Nowa cena:</strong><br />
            <span className="text-success">
              Netto: {directPriceNetto ? parseFloat(directPriceNetto).toFixed(2) : '0.00'} zł
            </span><br />
            <span className="text-success">
              Brutto: {directPriceBrutto ? parseFloat(directPriceBrutto).toFixed(2) : '0.00'} zł
            </span>
          </div>
        </div>
      </div>
    </BaseModal>
  );
};

export default DirectPriceModal;
